import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Input,
  Button,
  List,
  Avatar,
  Typography,
  Space,
  Alert,
  Spin,
  message,
} from 'antd';
import {
  RobotOutlined,
  UserOutlined,
  SendOutlined,
  SaveOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type { KrybotTemplate } from '../../../types/krybot';
import styles from './TemplateManagement.module.css';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface TemplatePromptBuilderTabProps {
  template: KrybotTemplate | null;
  templateData: Partial<KrybotTemplate>;
  isCreating: boolean;
  onDataChange: (updates: Partial<KrybotTemplate>) => void;
  onSave: () => void;
  loading: boolean;
}

export const TemplatePromptBuilderTab: React.FC<TemplatePromptBuilderTabProps> = ({
  template,
  templateData,
  isCreating,
  onDataChange,
  onSave,
  loading,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initialize with template context if available
    if (template && messages.length === 0) {
      const contextMessage: Message = {
        id: 'context',
        role: 'assistant',
        content: `I'm here to help you build and refine the template "${template.template_key}".

Current template details:
- **Template Key**: ${template.template_key}
- **Type**: ${template.template_type || 'Not set'}
- **Sector**: ${template.sector || 'Not set'}
- **Prompt Text**: ${template.prompt_text?.substring(0, 200) || 'Not set'}...

You can ask me to:
- Improve the prompt text
- Suggest better variable names
- Review the template structure
- Help with metadata configuration

What would you like to work on?`,
        timestamp: new Date(),
      };
      setMessages([contextMessage]);
    } else if (isCreating && messages.length === 0) {
      const contextMessage: Message = {
        id: 'context',
        role: 'assistant',
        content: `I'm here to help you build a new template from scratch.

I'll help you create:
- **Template Key** - Unique identifier
- **Template Type** - Category/type of template
- **Prompt Text** - The actual template with variables
- **Metadata** - Additional configuration

Let's start by understanding what this template will be used for. What is the template's purpose?`,
        timestamp: new Date(),
      };
      setMessages([contextMessage]);
    }
  }, [template, isCreating]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const buildTemplateContext = () => {
    return {
      template_key: templateData.template_key || template?.template_key || '',
      template_type: templateData.template_type || template?.template_type || '',
      sector: templateData.sector || template?.sector || '',
      prompt_text: templateData.prompt_text || template?.prompt_text || '',
      metadata: templateData.metadata || template?.metadata || {},
    };
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setChatLoading(true);

    try {
      const templateContext = buildTemplateContext();
      
      // Build messages array for Krybot API
      const apiMessages = messages
        .filter(msg => msg.id !== 'context') // Exclude context message
        .map((msg) => ({
          role: msg.role,
          content: msg.content,
        }));
      
      // Add the current user message
      apiMessages.push({
        role: 'user',
        content: userInput,
      });

      // Add system context about the template
      const systemContext = `You are helping to build a prompt template. Here is the current template configuration:

Template Key: ${templateContext.template_key || 'Not set'}
Template Type: ${templateContext.template_type || 'Not set'}
Sector: ${templateContext.sector || 'Not set'}

Current Prompt Text:
${templateContext.prompt_text || 'Not set'}

Current Metadata:
${JSON.stringify(templateContext.metadata, null, 2)}

You can suggest improvements to the prompt text or metadata. When suggesting changes, be specific and provide the complete updated value.`;

      // Call Krybot API via prompt builder endpoint
      const response = await KrybotService.promptBuilderChat({
        messages: [
          { role: 'system', content: systemContext },
          ...apiMessages,
        ],
      });

      const messageContent = response.message || 'I apologize, but I could not generate a response.';
      const rawContent = response.mapping?.raw || messageContent;
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: rawContent,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to process message';
      message.error(`Failed to process message: ${errorMessage}`);
      
      const errorChatMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I encountered an error: ${errorMessage}\n\nPlease check your Krybot API configuration or try again.`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorChatMessage]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleApplyToPrompt = (value: string) => {
    onDataChange({ prompt_text: value });
    message.success('Applied to prompt text');
  };

  return (
    <div className="template-prompt-builder-tab">
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Prompt Builder</h3>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={onSave}
          loading={loading}
        >
          Save Template Changes
        </Button>
      </div>
      <Alert
        message="AI-Powered Prompt Builder"
        description="Chat with AI to build and refine your template's prompt text. The AI has access to your current template configuration and can suggest improvements."
        type="info"
        style={{ marginBottom: 16 }}
      />

      <Card className="prompt-builder-chat-card" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 400px)', minHeight: '500px' }}>
        <div className="prompt-builder-messages" style={{ flex: 1, overflowY: 'auto', padding: '16px', marginBottom: '16px' }}>
          <List
            dataSource={messages}
            renderItem={(msg) => (
              <List.Item
                style={{
                  border: 'none',
                  padding: '12px 0',
                  backgroundColor: msg.role === 'user' ? 'transparent' : '#fafafa',
                }}
              >
                <Space align="start" style={{ width: '100%' }}>
                  <Avatar
                    icon={msg.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
                    style={{
                      backgroundColor: msg.role === 'user' ? '#1890ff' : '#52c41a',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <Text strong>{msg.role === 'user' ? 'You' : 'AI Assistant'}</Text>
                    <Paragraph style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Paragraph>
                    {msg.role === 'assistant' && (
                      <Space style={{ marginTop: 8 }}>
                        <Button
                          size="small"
                          icon={<CheckOutlined />}
                          onClick={() => {
                            // Try to extract prompt text from response
                            const lines = msg.content.split('\n');
                            const promptStart = lines.findIndex(l => 
                              l.toLowerCase().includes('prompt') || 
                              l.toLowerCase().includes('template')
                            );
                            if (promptStart >= 0) {
                              const promptText = lines.slice(promptStart + 1).join('\n').trim();
                              if (promptText.length > 50) {
                                handleApplyToPrompt(promptText);
                              }
                            } else {
                              // If no clear marker, use the whole response if it's substantial
                              if (msg.content.length > 100) {
                                handleApplyToPrompt(msg.content);
                              }
                            }
                          }}
                        >
                          Apply to Prompt
                        </Button>
                      </Space>
                    )}
                  </div>
                </Space>
              </List.Item>
            )}
          />
          <div ref={messagesEndRef} />
        </div>

        <div style={{ borderTop: '1px solid #e8e8e8', paddingTop: '16px', flexShrink: 0 }}>
          <Space.Compact style={{ width: '100%' }}>
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask AI to help build or improve your template..."
              rows={3}
              disabled={chatLoading || loading}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={chatLoading}
              disabled={loading}
              style={{ height: 'auto' }}
            >
              Send
            </Button>
          </Space.Compact>

          {chatLoading && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Spin />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

